import app from '../app';
import Page, { IPageAttrs } from '../../common/components/Page';
import ItemList from '../../common/utils/ItemList';
import LoadingIndicator from '../../common/components/LoadingIndicator';
import SelectDropdown from '../../common/components/SelectDropdown';
import LinkButton from '../../common/components/LinkButton';
import Separator from '../../common/components/Separator';
import type User from '../../common/models/User';
import type Mithril from 'mithril';
import type Post from '../../common/models/Post';
import Placeholder from '../../common/components/Placeholder';
import Button from '../../common/components/Button';
import Link from '../../common/components/Link';
import CommentPost from '../components/CommentPost';
import extractText from '../../common/utils/extractText';
import Switch from '../../common/components/Switch';
import FieldSet from '../../common/components/FieldSet';
import NotificationGrid from '../components/NotificationGrid';
import ChangePasswordModal from '../components/ChangePasswordModal';
import ChangeEmailModal from '../components/ChangeEmailModal';
import listItems from '../../common/helpers/listItems';

export interface ISettingPageAttrs extends IPageAttrs {}

/**
 * The `LikePage` component shows a user's profile. It can be extended to show
 * content inside of the content area. See `ActivityPage` and `SettingsPage` for
 * examples.
 *
 * @abstract
 */
export default class SettingPage<CustomAttrs extends ISettingPageAttrs = ISettingPageAttrs, CustomState = undefined> extends Page<CustomAttrs, CustomState> {
  /**
   * The user this page is for.
   */
  user: User | null = null;

  /**
   * Whether or not the activity feed is currently loading.
   */
  loading: boolean = true;

  /**
   * Whether or not there are any more activity items that can be loaded.
   */
  moreResults: boolean = false;

  /**
   * The Post models in the feed.
   */
  posts: Post[] = [];

  /**
   * The number of activity items to load per request.
   */
  loadLimit: number = 20;

  discloseOnlineLoading?: boolean;

  oninit(vnode: Mithril.Vnode<CustomAttrs, this>) {
    super.oninit(vnode);

    this.bodyClass = 'App--user';
    app.history.push(`${this.attrs.routeName}`);
    this.show(app.session.user!);

    // app.setTitle(extractText(app.translator.trans('core.forum.settings.title')));
    // this.loadUser(m.route.param('username'));
  }

  title() {
    return <h4 className="App-titleControl person-title">{app.translator.trans('core.forum.settings.title')}</h4>;
  }

  /**
   * Base view template for the user page.
   */
  view() {
    return (
      <div className="UserPage">
        {this.title()}
        {this.user
          ? [
              <div className="container extra-userPage-container setting-page">
                <div className="sideNavContainer">
                  <div className="sideNavOffset UserPage-content">{this.content()}</div>
                </div>
              </div>,
            ]
          : [<LoadingIndicator display="block" />]}
      </div>
    );
  }

  /**
   * Get the content to display in the user page.
   */
  content() {
    return (
      <div className="SettingsPage">
        <ul>{listItems(this.settingsItems().toArray())}</ul>
      </div>
    );
  }

  /**
   * Build an item list for the user's settings controls.
   */
  settingsItems() {
    const items = new ItemList<Mithril.Children>();

    ['account', 'notifications'].forEach((section, index) => {
      const sectionItems = `${section}Items` as 'accountItems' | 'notificationsItems';

      items.add(
        section,
        <FieldSet className={`Settings-${section}`} label={app.translator.trans(`core.forum.settings.${section}_heading`)}>
          {this[sectionItems]().toArray()}
        </FieldSet>,
        100 - index * 10
      );
    });

    return items;
  }

  /**
   * Build an item list for the user's account settings.
   */
  accountItems() {
    const items = new ItemList<Mithril.Children>();

    let user = app.session.user;
    if(!user.notHideChangePwd()) {
      items.add(
        'changePassword',
        <Button className="Button" onclick={() => app.modal.show(ChangePasswordModal)}>
          {app.translator.trans('core.forum.settings.change_password_button')}
        </Button>,
        100
      );
    }
    

    items.add(
      'changeEmail',
      <Button className="Button" onclick={() => app.modal.show(ChangeEmailModal)}>
        {app.translator.trans('core.forum.settings.change_email_button')}
      </Button>,
      90
    );

    items.add(
      'blackList',
      <LinkButton className="Button" href={app.route('user.ignoredUsers2')}>
        {app.translator.trans('dl-blackList.forum.blackList_button')}
      </LinkButton>,
      80
    );

    return items;
  }

  /**
   * Build an item list for the user's notification settings.
   */
  notificationsItems() {
    const items = new ItemList<Mithril.Children>();

    items.add('notificationGrid', <NotificationGrid user={this.user} />, 100);

    return items;
  }

  /**
   * Build an item list for the user's privacy settings.
   */
  privacyItems() {
    const items = new ItemList<Mithril.Children>();

    // items.add(
    //   'discloseOnline',
    //   <Switch
    //     state={this.user!.preferences()?.discloseOnline}
    //     onchange={(value: boolean) => {
    //       this.discloseOnlineLoading = true;

    //       this.user!.savePreferences({ discloseOnline: value }).then(() => {
    //         this.discloseOnlineLoading = false;
    //         m.redraw();
    //       });
    //     }}
    //     loading={this.discloseOnlineLoading}
    //   >
    //     {app.translator.trans('core.forum.settings.privacy_disclose_online_label')}
    //   </Switch>,
    //   100
    // );

    return items;
  }

  /**
   * Initialize the component with a user, and trigger the loading of their
   * activity feed.
   *
   * @protected
   */
  show(user: User): void {
    this.user = user;

    app.current.set('user', user);

    app.setTitle(user.displayName());

    m.redraw();
  }

  /**
   * Given a username, load the user's profile from the store, or make a request
   * if we don't have it yet. Then initialize the profile page with that user.
   */
  loadUser(username: string) {
    const lowercaseUsername = username.toLowerCase();

    // Load the preloaded user object, if any, into the global app store
    // We don't use the output of the method because it returns raw JSON
    // instead of the parsed models
    app.preloadedApiDocument();

    app.store.all<User>('users').some((user) => {
      if ((user.username().toLowerCase() === lowercaseUsername || user.id() === username) && user.joinTime()) {
        this.show(user);
        return true;
      }

      return false;
    });

    if (!this.user) {
      app.store.find<User>('users', username, { bySlug: true }).then(this.show.bind(this));
    }
  }

}
